import React, { useState, useEffect } from 'react';
import cn from 'classnames';

import tableSort from 'js/utils/sort-table';

import ThWithSort from './ThWithSort';

import { ITable } from 'source/NFR.types';

// Checks whether a string represents a number, to allow for special styling of numbers-as-text
const isNumber = (string = '') => !isNaN(Number(string.replace(/\s/g, '')));

const Table: React.FC<ITable> = ({ header, rows, isSortingEnabled = false, sortingIndex = 0, title }) => {
  const [rowsState, setRowsState] = useState(rows);
  const [sortDirection, setSortDirection] = useState<(boolean | undefined)[]>(rows[0].map(() => undefined));

  const sort = (index: number, initiallyReverse = false) => {
    setRowsState(rows => {
      const rowsCopy = rows.map(row => [...row]);
      rowsCopy.sort((a, b) => tableSort(a[index].value, b[index].value));

      const shouldReverse = sortDirection[index] || initiallyReverse;
      if (shouldReverse) {
        rowsCopy.reverse();
      }
      return rowsCopy;
    });

    setSortDirection(prevState =>
      prevState.map((shouldRev, i) => (i === index ? !(shouldRev || initiallyReverse) : undefined))
    );
  };

  useEffect(() => {
    if (isSortingEnabled) {
      sort(sortingIndex, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={cn('table', { 'table--print': isSortingEnabled })}>
      <table data-ignore-table>
        <caption>{title}</caption>
        {header.length > 0 && (
          <thead>
            <tr>
              {header.map((column, index) => (
                <React.Fragment key={column + index}>
                  {isSortingEnabled ? (
                    <ThWithSort onClick={() => sort(index)} sortDirection={sortDirection[index]}>
                      {column}
                    </ThWithSort>
                  ) : (
                    <th>{column}</th>
                  )}
                </React.Fragment>
              ))}
            </tr>
          </thead>
        )}
        {rowsState.length > 0 && (
          <tbody>
            {rowsState.map((row = [], index) => (
              <tr key={index + row[0].value}>
                {row?.map(({ value, displayValue, isRightAligned }, index) => (
                  <td
                    className={cn({
                      'table__cell--is-number': isNumber(value),
                      'table__cell--is-right-aligned': isRightAligned
                    })}
                    key={value + index}
                  >
                    {displayValue || value}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        )}
      </table>
    </div>
  );
};

Table.displayName = 'Table';

export default Table;
